import api from "@/api";

export default {
    namespaced: true,
    state: () => ({
        students: [],
        currentStudent: null,
        isLoading: false,
        error: null,
        filters: {
            search: '',
            class_id: null,
            gender: null
        },
        pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: 10,
            from: 0,
            to: 0
        }
    }),
    mutations: {
        SET_STUDENTS(state, students) {
            state.students = students;
        },
        SET_CURRENT_STUDENT(state, student) {
          state.currentStudent = student
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_FILTERS(state, filters) {
            state.filters = { ...state.filters, ...filters };
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = { ...state.pagination, ...pagination };
        },
        RESET_FILTERS(state) {
            state.filters = {
                search: '',
                class_id: null,
                gender: null
            };
        },
        ADD_STUDENT(state, student) {
          state.students.unshift(student);
        },
        UPDATE_STUDENT(state, updatedStudent) {
          const index = state.students.findIndex(s => s.id === updatedStudent.id);
          if (index !== -1) {
            state.students.splice(index, 1, updatedStudent);
          }
        },
        DELETE_STUDENT(state, studentId) {
          state.students = state.students.filter(s => s.id !== studentId);
        }
    },
    actions: {
        async fetchStudents({ commit, state }, { page = 1, perPage = 15 } = {}) {
            try {
                commit('SET_LOADING', true);
                const params = {
                    page,
                    per_page: perPage,
                    ...state.filters
                };

                const response = await api.get('/students', { params });
                
                commit('SET_STUDENTS', response.data.data);
                commit('SET_PAGINATION', {
                    currentPage: response.data.meta.current_page,
                    totalPages: response.data.meta.last_page,
                    totalItems: response.data.meta.total,
                    perPage: response.data.meta.per_page,
                    from: response.data.meta.from,
                    to: response.data.meta.to
                });
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async fetchStudent({ commit }, studentId) {
          try {
            commit('SET_LOADING', true);
            const response = await api.get(`/students/${studentId}`, {
              params: {
                include: 'classGroups.class,currentAssignment.class,currentAssignment.group,nationality'
              }
            });
            
            const student = response.data.data || response.data;
            
            // Normalize the data structure
            const currentAssignment = student.current_assignment || 
                                   (student.class_groups && student.class_groups[0]) || 
                                   null;
        
            const studentData = {
              ...student,
              current_assignment: currentAssignment,
              class_id: currentAssignment?.class?.id || null,
              group_id: currentAssignment?.group?.id || currentAssignment?.id || null,
              // Ensure all fields are properly initialized
              nationality_id: student.nationality?.id || '',
              image_url: student.image_url || null,
              register_date: student.register_date || new Date().toISOString().split('T')[0]
            };
        
            commit('SET_CURRENT_STUDENT', studentData);
            return studentData;
          } catch (error) {
            commit('SET_ERROR', error.response?.data?.message || error.message);
            throw error;
          } finally {
            commit('SET_LOADING', false);
          }
        },
        async createStudent({ commit }, studentData) {
          try {
            commit('SET_LOADING', true);
            const response = await api.post('/students', studentData);
            commit('ADD_STUDENT', response.data);
            return response.data;
          } catch (error) {
            commit('SET_ERROR', error.response?.data?.message || error.message);
            throw error;
          } finally {
            commit('SET_LOADING', false);
          }
        },
        async updateStudent({ commit, dispatch }, { id, studentData }) {
          try {
            commit('SET_LOADING', true);
            
            const formData = new FormData();
            
            // Append all fields
            Object.keys(studentData).forEach(key => {
              if (studentData[key] !== null && studentData[key] !== undefined) {
                if (key === 'image' && studentData[key] instanceof File) {
                  formData.append('image', studentData[key]);
                } else if (key !== 'image_url') { 
                                    formData.append(key, studentData[key]);
                }
              }
            });
        
            // Ensure class/group are included
            if (studentData.class_id) {
              formData.append('class_id', studentData.class_id);
            }
            if (studentData.group_id) {
              formData.append('group_id', studentData.group_id);
            }
        
            formData.append('_method', 'PUT');
        
            await api.post(`/students/${id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
        
            // Refresh the student data
            const updatedStudent = await dispatch('fetchStudent', id);
            commit('UPDATE_STUDENT', updatedStudent);
            
            return updatedStudent;
          } catch (error) {
            commit('SET_ERROR', error.response?.data?.message || error.message);
            throw error;
          } finally {
            commit('SET_LOADING', false);
          }
        },
async deleteStudent({ commit }, studentId) {
  commit('SET_LOADING', true);
  try {
    const res = await api.delete(`/students/${studentId}`);
    commit('DELETE_STUDENT', studentId);
    return res; 
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to delete student';
    commit('SET_ERROR', message);

    throw error;
  } finally {
    commit('SET_LOADING', false);
  }
}

    

    },
    getters: {
        filteredStudents: state => state.students,
        isLoading: state => state.isLoading,
        getError: state => state.error,
        getFilters: state => state.filters,
        getPagination: state => state.pagination
    }
};